<script setup lang="ts">
import { canonicalCinemaRoute } from "./utils/workspaceRoute";
import { computed, onMounted, ref, watch } from 'vue'
import { AgentChatPanel, message } from '@xjlv2/admin-layout'

// 基础外壳与工作台布局
import LoginView from './views/auth/LoginView.vue'
import WorkspaceDashboard from './views/dashboard/WorkspaceDashboard.vue'
import ShellLayout from './components/layout/ShellLayout.vue'
import ModuleHost from './components/layout/ModuleHost.vue'
import NotificationBell from './components/notification/NotificationBell.vue'
import NotificationCenter from './components/notification/NotificationCenter.vue'
import WechatBindingCard from './components/wechat/WechatBindingCard.vue'

// 业务领域组件
import PushCenterManager from './views/push/PushCenterManager.vue'
import SaasDecorationManager from './views/decoration/SaasDecorationManager.vue'
import ContentReviewCenter from './views/content-review/ContentReviewCenter.vue'
import MaterialManager from './views/material/MaterialManager.vue'
import BehaviorAnalyticsView from './views/analytics/BehaviorAnalyticsView.vue'
import CrashIssuesView from './components/CrashIssuesView.vue'

// 系统管理与权限体系
import {
  ClientRoleManager,
  ClientUserManager,
  DeptManager,
  DictManager,
  MenuManager,
  ModuleUserManager,
  OpenPlatformGrantManager,
  PermissionCenter,
  RoleManager,
  SystemParamManager,
  UserManager
} from './views/system'
import { useNotificationCenter } from './composables/useNotificationCenter'
import { useRbacActionHandlers } from './composables/useRbacActionHandlers'
import { useSaasAgent } from './composables/useSaasAgent'
import { useSaasWorkspace } from './composables/useSaasWorkspace'
import {
  MATERIAL_CENTER_MENU_CODE,
  canonicalWorkspaceMenuCode,
  decorationHomeMenuCode,
  decorationMarketMenuCode,
  decorationPageCodes,
  decorationPageList,
  decorationPagesMenuCode,
  legacyMaterialScope,
  resolveDecorationDefaultPageCode,
  resolveModuleWorkbenchNavigation,
  resolveShellMenuKind
} from './utils/menuArchitecture'
import type {
  CreateDictItemRequest,
  CreateDictTypeRequest,
  CreateDeptRequest,
  CreateClientRoleRequest,
  CreateClientUserRequest,
  CreateMenuRequest,
  CreateModuleUserRequest,
  CreateParamRequest,
  CreateRoleRequest,
  CreateUserRequest,
  MenuReorderItem,
  ModuleNotificationPayload,
  ModuleMenuItem,
  PermissionManagementSection,
  ShellNotification,
  ShellNotificationTarget,
  UpdateDictItemRequest,
  UpdateDictTypeRequest,
  UpdateDeptRequest,
  UpdateClientRoleRequest,
  UpdateClientUserRequest,
  UpdateModuleUserRequest,
  UpdateModuleUserStatusRequest,
  UpdateMenuRequest,
  UpdateParamRequest,
  UpdateRoleRequest,
  UpdateTenantOpenAppGrantRequest,
  WorkspaceTab
} from './types'
import { DECORATION_PERMISSIONS, SYSTEM_PERMISSIONS, CRASH_ISSUES_VIEW_PERMISSION, pickModuleLocateTarget } from '@xjlv2/api-client'
import { resolveRequestedTenantDomain as resolveTenantDomain } from './utils/tenantBootstrap'
import { reportShellPageView, shellTrafficRouteFor } from './traffic/traffic-pages'
import { reportShellCrashBreadcrumb } from './crash/crash-breadcrumbs'
import { normalizeUnavailableMenuRoute } from './utils/workspaceRoute'

const activeTab = ref<WorkspaceTab>('dashboard')
const activePermissionSection = ref<PermissionManagementSection>('permissions')
const activeMenu = ref<ModuleMenuItem | null>(null)
const moduleLocateTarget = ref<ShellNotificationTarget | null>(null)
const activeModuleHost = ref<InstanceType<typeof ModuleHost> | null>(null)
const activeDecorationManager = ref<InstanceType<typeof SaasDecorationManager> | null>(null)
const decorationPageCode = ref('home')
const decorationWorkspaceMode = ref<'pages' | 'editor'>('pages')
const activePushCenterManager = ref<InstanceType<typeof PushCenterManager> | null>(null)
const activeMaterialManager = ref<InstanceType<typeof MaterialManager> | null>(null)
const activeContentReview = ref<InstanceType<typeof ContentReviewCenter> | null>(null)
const materialScopeModuleCode = ref('')
const notice = ref('')
const wechatScanEnabled = ref(false)
const workspace = useSaasWorkspace()
const session = workspace.session
const notificationCenter = useNotificationCenter({
  createApiClient: session.createApiClient,
  hasPermission: session.hasPermission,
  resolveModuleMenu,
  openTarget: openNotificationTarget,
  onError: (message) => {
    workspace.error.value = message
  }
})

const rbacHandlers = useRbacActionHandlers({
  workspace,
  setNotice: (message) => {
    notice.value = message
  }
})

const {
  createRole,
  updateRole,
  createUser,
  createClientRole,
  updateClientRole,
  createClientUser,
  updateClientUser,
  createModuleUser,
  updateModuleUser,
  updateModuleUserStatus,
  createDept,
  updateDept,
  createParam,
  updateParam,
  deleteParam,
  createDict,
  updateDict,
  deleteDict,
  createDictItem,
  updateDictItem,
  deleteDictItem,
  reorderDictItems,
  createMenu,
  updateMenu,
  reorderMenus,
  deleteMenu,
  updateOpenAppGrant
} = rbacHandlers

type FallbackMenuDefinition = Pick<ModuleMenuItem, 'code' | 'parentCode' | 'name' | 'icon' | 'path' | 'permission'>

const GUOXUE_REMOTE_ENTRY = '/mfe/guoxue-saas/remoteEntry.js'
const GUOXUE_EXPOSED_MODULE = './GuoxueApp'
const guoxueFallbackMenus: Record<string, FallbackMenuDefinition> = {
  'guoxue.wallet': {
    code: 'guoxue.wallet',
    parentCode: 'guoxue.console',
    name: '钱包财务',
    icon: 'wallet-cards',
    path: '/guoxue/wallet',
    permission: 'guoxue:wallet:view'
  },
  'guoxue.wallet.accounts': {
    code: 'guoxue.wallet.accounts',
    parentCode: 'guoxue.wallet',
    name: '用户钱包',
    icon: 'wallet',
    path: '/guoxue/wallet/accounts',
    permission: 'guoxue:wallet:view'
  },
  'guoxue.wallet.transactions': {
    code: 'guoxue.wallet.transactions',
    parentCode: 'guoxue.wallet',
    name: '资金流水',
    icon: 'list-checks',
    path: '/guoxue/wallet/transactions',
    permission: 'guoxue:wallet:view'
  },
  'guoxue.wallet.recharge': {
    code: 'guoxue.wallet.recharge',
    parentCode: 'guoxue.wallet',
    name: '充值订单',
    icon: 'arrow-up-circle',
    path: '/guoxue/wallet/recharges',
    permission: 'guoxue:wallet:manage'
  },
  'guoxue.shop.coupon': {
    code: 'guoxue.shop.coupon',
    parentCode: 'guoxue.shop',
    name: '优惠券',
    icon: 'ticket-percent',
    path: '/guoxue/shop/coupons',
    permission: 'guoxue:product:manage'
  }
}

const activeTabAllowed = computed(() =>
  activeMenu.value !== null || workspace.availableTabs.value.some((tab) => tab.key === activeTab.value)
)
const activeMenuIsModuleUserManagement = computed(() => isModuleUserManagementMenu(activeMenu.value))
const activeMenuIsDecoration = computed(() => activeMenu.value?.kind === 'decoration')
const activeDecorationMenuModuleCode = computed(() => activeMenu.value?.moduleCode || 'decoration')
const activeDecorationModuleCode = computed(() => activeMenuIsDecoration.value
  ? activeDecorationMenuModuleCode.value
  : 'decoration')
const activeDecorationApiModuleCode = computed(() => activeDecorationModuleCode.value === 'decoration'
  ? ''
  : activeDecorationModuleCode.value)
const activeMenuIsPushCenter = computed(() => activeMenu.value?.moduleCode === 'push')
const activeMenuIsMaterial = computed(() => activeMenu.value?.kind === 'material')
const activeMenuIsContentReview = computed(() => activeMenu.value?.kind === 'content-review')
const materialModuleOptions = computed(() =>
  workspace.permittedModules.value
    .filter((module) => !['material-center', 'qrcode'].includes(module.moduleCode))
    .map((module) => ({ code: module.moduleCode, name: module.moduleName }))
)
const activeDecorationLeftTab = computed<'components' | 'templates'>(() =>
  activeMenu.value?.code === decorationMarketMenuCode(activeDecorationMenuModuleCode.value) ? 'templates' : 'components'
)
const activeDecorationMode = computed<'pages' | 'editor' | 'templates'>(() => {
  if (activeMenu.value?.code === decorationMarketMenuCode(activeDecorationMenuModuleCode.value)) return 'templates'
  if (activeMenu.value?.code === decorationPagesMenuCode(activeDecorationMenuModuleCode.value)) return decorationWorkspaceMode.value
  return 'editor'
})
const activeDecorationCanView = computed(() => session.hasPermission(
  activeDecorationApiModuleCode.value
    ? `${activeDecorationApiModuleCode.value}:decoration:view`
    : DECORATION_PERMISSIONS.pageView
))
const activeDecorationCanManage = computed(() => session.hasPermission(
  activeDecorationApiModuleCode.value
    ? `${activeDecorationApiModuleCode.value}:decoration:manage`
    : DECORATION_PERMISSIONS.pageManage
))
const activeDecorationCanManageTemplates = computed(() => activeDecorationApiModuleCode.value
  ? activeDecorationCanManage.value
  : session.hasPermission(DECORATION_PERMISSIONS.templateManage))
const activeModuleUserModule = computed(() => {
  if (!activeMenuIsModuleUserManagement.value) return null
  return (
    workspace.permittedModules.value.find((module) => module.moduleCode === activeMenu.value?.moduleCode) ??
    null
  )
})
const activeModuleUserModules = computed(() =>
  activeModuleUserModule.value ? [activeModuleUserModule.value] : []
)
const saasAgentContext = computed<Record<string, unknown>>(() => ({
  activeTab: activeTab.value,
  activePermissionSection: activePermissionSection.value,
  moduleCode: activeMenu.value?.moduleCode,
  menuCode: activeMenu.value?.code,
  path: activeMenu.value?.path,
  tenantId: session.user?.tenantId,
  userId: session.user?.userId
}))

const saasAgent = useSaasAgent({
  createApiClient: session.createApiClient,
  hasPermission: session.hasPermission,
  getContext: () => saasAgentContext.value,
  onOpenTarget: openNotificationTarget,
  onRefreshView: refreshCurrentView,
  onError: (message) => {
    workspace.error.value = message
  },
  onNotice: (message) => {
    notice.value = message
  }
})
const permissionSections = computed(() =>
  [
    {
      key: 'permissions' as const,
      label: '权限列表',
      enabled: workspace.canViewPermissions.value
    },
    {
      key: 'menus' as const,
      label: '菜单列表',
      enabled: workspace.canReadMenus.value
    },
    {
      key: 'depts' as const,
      label: '部门列表',
      enabled: workspace.canReadDepts.value
    },
    {
      key: 'roles' as const,
      label: '角色列表',
      enabled: workspace.canReadRbac.value
    },
    {
      key: 'users' as const,
      label: '管理列表',
      enabled: workspace.canReadRbac.value
    },
    {
      key: 'clientRoles' as const,
      label: 'C端角色',
      enabled: workspace.canReadClientRbac.value
    },
    {
      key: 'clientUsers' as const,
      label: 'C端用户',
      enabled: workspace.canReadClientRbac.value
    },
    {
      key: 'moduleUsers' as const,
      label: '模块用户',
      enabled: workspace.canReadModuleUsers.value
    },
    {
      key: 'params' as const,
      label: '参数管理',
      enabled: workspace.canReadParams.value
    },
    {
      key: 'dicts' as const,
      label: '字典管理',
      enabled: workspace.canReadDicts.value
    },
    {
      key: 'openPlatform' as const,
      label: '开放授权列表',
      enabled: workspace.canReadOpenApps.value
    }
  ].filter((section) => section.enabled)
)

watch(workspace.availableTabs, (tabs) => {
  if (!tabs.some((tab) => tab.key === activeTab.value)) {
    activeTab.value = tabs[0]?.key ?? 'dashboard'
  }
})

watch(permissionSections, (sections) => {
  if (!sections.some((section) => section.key === activePermissionSection.value)) {
    activePermissionSection.value = sections[0]?.key ?? 'permissions'
  }
})

watch([activeTab, activePermissionSection, activeMenu, decorationPageCode, decorationWorkspaceMode, materialScopeModuleCode, moduleLocateTarget], () => {
  persistWorkspaceRoute()
})

watch([activeTab, activePermissionSection, activeMenu], () => {
  reportCurrentWorkspacePage()
})

function reportCurrentWorkspacePage() {
  // 流量上报是旁路观察：off（未装配）或未登录时为空操作，不影响既有渲染与导航。
  const route = shellTrafficRouteFor({
    moduleMenuActive: activeMenu.value !== null,
    tab: activeTab.value,
    permissionSection: activePermissionSection.value
  })
  reportShellPageView(route)
  // crash 面包屑同点记录导航现场（traffic observer 同点；off 时同样为空操作）。
  reportShellCrashBreadcrumb('navigation', route)
}

onMounted(async () => {
  try {
    await session.restore(resolveTenantDomain(window.location.search, window.location.hostname))
  } catch (err) {
    workspace.error.value = err instanceof Error ? err.message : '登录状态恢复失败'
  }
  await workspace.refreshWorkspace()
  restoreWorkspaceRoute()
  reportCurrentWorkspacePage()
  try {
    await notificationCenter.loadNotifications()
  } catch {
    // 忽略未读通知加载失败，避免阻断工作区启动
  }
  await saasAgent.loadModelStatus()
  await loadWechatScanEnabled()
})

async function login(username: string, password: string, tenantDomain: string) {
  workspace.loading.value = true
  workspace.error.value = ''
  notice.value = ''
  try {
    await session.login(username, password, tenantDomain)
    await workspace.refreshWorkspace()
    restoreWorkspaceRoute()
    reportCurrentWorkspacePage()
    try {
      await notificationCenter.loadNotifications()
    } catch {
      // 忽略未读通知加载失败
    }
    await saasAgent.loadModelStatus()
    await loadWechatScanEnabled()
  } catch (err) {
    workspace.error.value = err instanceof Error ? err.message : '登录失败'
  } finally {
    workspace.loading.value = false
  }
}

async function loginFromResponse(response: any) {
  workspace.loading.value = true
  workspace.error.value = ''
  notice.value = ''
  try {
    await session.loginFromResponse(response)
    await workspace.refreshWorkspace()
    restoreWorkspaceRoute()
    await notificationCenter.loadNotifications()
    await saasAgent.loadModelStatus()
    await loadWechatScanEnabled()
  } catch (err) {
    workspace.error.value = err instanceof Error ? err.message : '登录失败'
  } finally {
    workspace.loading.value = false
  }
}

function logout() {
  session.logout()
  notificationCenter.notifications.value = []
  activeTab.value = 'dashboard'
  activeMenu.value = null
  decorationPageCode.value = 'home'
  decorationWorkspaceMode.value = 'pages'
  notice.value = ''
  saasAgent.reset()
  wechatScanEnabled.value = false
}

function needsConfiguredMenus(section: PermissionManagementSection) {
  return section === 'menus' || section === 'roles' || section === 'users'
}

async function loadConfiguredMenus(section: PermissionManagementSection) {
  if (!needsConfiguredMenus(section)) return
  try {
    await workspace.loadMenus()
  } catch (err) {
    workspace.error.value = err instanceof Error ? err.message : '菜单加载失败'
  }
}

async function setActiveTab(tab: WorkspaceTab) {
  activeTab.value = tab
  activeMenu.value = null
  if (tab === 'permissionManagement') {
    activePermissionSection.value = permissionSections.value[0]?.key ?? 'permissions'
    await loadConfiguredMenus(activePermissionSection.value)
  }
}

async function setPermissionSection(section: PermissionManagementSection) {
  activeTab.value = 'permissionManagement'
  activeMenu.value = null
  activePermissionSection.value = section
  notice.value = ''
  await loadConfiguredMenus(section)
}

function openModuleMenu(menu: ModuleMenuItem, locate?: ShellNotificationTarget | null) {
  moduleLocateTarget.value = pickModuleLocateTarget(locate ?? undefined) ?? null
  if (menu.code.endsWith('.materials') && menu.code !== MATERIAL_CENTER_MENU_CODE) {
    const materialMenu = workspace.menuViews.value
      .flatMap((module) => module.visibleMenus)
      .find((item) => item.code === MATERIAL_CENTER_MENU_CODE && !item.hasChildren)
    if (!materialMenu) {
      workspace.error.value = '当前账号没有素材中心权限。'
      return
    }
    materialScopeModuleCode.value = legacyMaterialScope(menu.code)
    activeMenu.value = materialMenu
    notice.value = ''
    return
  }
  if (menu.kind === 'decoration' && menu.code === decorationHomeMenuCode(menu.moduleCode)) {
    // 模块作用域装修首页：模块目录页码是模块自有约定（如 OPC 的 opc-home），
    // 先取模块装修页列表解析合法默认页码再打开编辑器（G4：通用约定 home/app
    // 不在模块白名单内，直接请求会被 DECORATION_PAGE_CODE_INVALID 拒绝）
    if (menu.moduleCode && menu.moduleCode !== 'decoration') {
      void openModuleScopedDecorationHome(menu.moduleCode)
    } else {
      openDecorationPageEditor('home', menu.moduleCode)
    }
    return
  }
  if (menu.kind === 'decoration' && menu.code === decorationPagesMenuCode(menu.moduleCode)) {
    decorationWorkspaceMode.value = 'pages'
  }
  activeMenu.value = menu
  notice.value = ''
}

function openWorkbenchModule(moduleCode: string) {
  const navigation = resolveModuleWorkbenchNavigation(
    workspace.menuViews.value.flatMap((module) => module.visibleMenus),
    moduleCode
  )
  if (!navigation.menu) {
    workspace.error.value = navigation.error
    return
  }
  workspace.error.value = ''
  openModuleMenu(navigation.menu)
}

function openDecorationPageEditor(pageCode: string, menuModuleCode = activeDecorationMenuModuleCode.value) {
  const normalizedPageCode = normalizeDecorationPageCode(pageCode)
  if (!normalizedPageCode) return
  const menus = workspace.menuViews.value.flatMap((module) => module.visibleMenus)
  const editorMenu = findOpenableMenu(menus, decorationPagesMenuCode(menuModuleCode))
    ?? findOpenableMenu(menus, decorationHomeMenuCode(menuModuleCode))
  if (!editorMenu) {
    workspace.error.value = '当前账号没有页面管理权限。'
    return
  }
  decorationPageCode.value = normalizedPageCode
  decorationWorkspaceMode.value = 'editor'
  activeMenu.value = editorMenu
  notice.value = ''
}

/**
 * 模块作用域装修首页入口（G4 契约修复）：先调模块装修页列表端点
 * （GET /api/saas/{module}/decoration/pages，权限与编辑器同码），按
 * <module>-home 优先解析模块白名单内的合法默认页码，再打开编辑器；
 * 列表为空/接口失败时给出可读提示，不按通用约定 home 盲发请求。
 */
async function openModuleScopedDecorationHome(menuModuleCode: string) {
  const menus = workspace.menuViews.value.flatMap((module) => module.visibleMenus)
  const editorMenu = findOpenableMenu(menus, decorationPagesMenuCode(menuModuleCode))
    ?? findOpenableMenu(menus, decorationHomeMenuCode(menuModuleCode))
  if (!editorMenu) {
    workspace.error.value = '当前账号没有页面管理权限。'
    return
  }
  try {
    const pages = decorationPageList(await session.createApiClient().getSaasModuleDecorationPages(menuModuleCode))
    const defaultPageCode = resolveDecorationDefaultPageCode(menuModuleCode, decorationPageCodes(pages))
    if (!defaultPageCode) {
      workspace.error.value = '该模块尚未配置装修页面。'
      return
    }
    decorationPageCode.value = defaultPageCode
    decorationWorkspaceMode.value = 'editor'
    activeMenu.value = editorMenu
    notice.value = ''
  } catch (err) {
    workspace.error.value = err instanceof Error ? err.message : '加载模块装修页面失败'
  }
}

function setDecorationPageCode(pageCode: string) {
  const normalizedPageCode = normalizeDecorationPageCode(pageCode)
  if (normalizedPageCode) decorationPageCode.value = normalizedPageCode
}

function normalizeDecorationPageCode(pageCode: string | null | undefined) {
  const normalized = String(pageCode || '').trim().toLowerCase()
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized) && normalized.length <= 48
    ? normalized
    : ''
}

function isModuleUserManagementMenu(menu: ModuleMenuItem | null) {
  if (!menu) return false
  if (menu.kind === 'module-user') return true
  return (
    menu.moduleCode === 'guoxue' &&
    (menu.code === 'guoxue.user.member' || menu.path === '/guoxue/users/members')
  )
}

function restoreWorkspaceRoute() {
  if (!session.user) return
  const legacyCinema = canonicalCinemaRoute(window.location.pathname, window.location.search)
  if (legacyCinema) window.history.replaceState(null, '', `${legacyCinema.pathname}?${legacyCinema.search}${window.location.hash}`)
  const query = new URLSearchParams(window.location.search)
  const menuCode = query.get('menuCode')?.trim()
  const permissionSection = query.get('permissionSection')?.trim() as PermissionManagementSection | null
  const tab = query.get('tab')?.trim() as WorkspaceTab | null
  if (menuCode) {
    const menus = workspace.menuViews.value.flatMap((module) => module.visibleMenus)
    const isLegacyAppShell = menuCode === 'decoration.app-shell'
    const canonicalMenuCode = canonicalWorkspaceMenuCode(menuCode)
    const requestedMenu = findOpenableMenu(menus, canonicalMenuCode)
    const requestedModuleCode = requestedMenu?.moduleCode
      || (canonicalMenuCode.startsWith('guoxue.') ? 'guoxue' : 'decoration')
    const isDecorationHome = canonicalMenuCode === decorationHomeMenuCode(requestedModuleCode)
    const resolvedMenuCode = isDecorationHome
      ? decorationPagesMenuCode(requestedModuleCode)
      : canonicalMenuCode
    const menu = findOpenableMenu(menus, resolvedMenuCode)
    if (menu) {
      if (menu.code === MATERIAL_CENTER_MENU_CODE) {
        materialScopeModuleCode.value = query.get('materialScope')?.trim()
          || legacyMaterialScope(menuCode)
          || materialScopeModuleCode.value
      }
      if (isLegacyAppShell) {
        decorationPageCode.value = 'app'
        decorationWorkspaceMode.value = 'editor'
      } else if (isDecorationHome) {
        decorationPageCode.value = normalizeDecorationPageCode(query.get('pageCode')) || 'home'
        decorationWorkspaceMode.value = 'editor'
      } else if (menu.code === decorationPagesMenuCode(menu.moduleCode)) {
        decorationWorkspaceMode.value = query.get('decorationMode') === 'editor' ? 'editor' : 'pages'
        decorationPageCode.value = normalizeDecorationPageCode(query.get('pageCode')) || 'home'
      }
      activeMenu.value = menu
      moduleLocateTarget.value = pickModuleLocateTarget({
        view: query.get('view') ?? undefined,
        paymentModule: query.get('paymentModule') ?? undefined,
        mchOrderNo: query.get('mchOrderNo') ?? undefined,
        payOrderNo: query.get('payOrderNo') ?? undefined,
        planId: query.get('planId') ?? undefined,
        returnMenuCode: query.get('returnMenuCode') ?? undefined
      }) ?? null
      return
    }
    // saas-shell: 菜单未找到时，可能是接口慢/超时导致数据尚未加载完成
    // 延迟重试而非立即报错，避免用户看到误导性的"未开通该产品"提示
    if (session.signedIn && retryCount < MAX_RETRIES) {
      retryCount++
      setTimeout(() => {
        restoreWorkspaceRoute()
      }, 2000)
      return
    }
    denyUnavailableMenuRoute(menuCode)
    retryCount = 0
    return
  }
  retryCount = 0
  if (permissionSection && permissionSections.value.some((section) => section.key === permissionSection)) {
    activeTab.value = 'permissionManagement'
    activePermissionSection.value = permissionSection
    activeMenu.value = null
    void loadConfiguredMenus(permissionSection)
    return
  }
  if (tab && workspace.availableTabs.value.some((item) => item.key === tab)) {
    activeTab.value = tab
    activeMenu.value = null
  }
}

// saas-shell: 延迟重试机制，用于处理 deep-link 场景下接口慢导致数据未就绪的时序问题
// 每 2 秒重试一次，最多重试 10 次（20 秒）
let retryCount = 0
const MAX_RETRIES = 10

// saas-shell: 仅弹出错误提示，不清空当前菜单、不跳转工作台，避免阻断用户正在访问的页面
function denyUnavailableMenuRoute(menuCode: string) {
  const menusLoaded = workspace.menuViews.value.some((module) => module.visibleMenus.length > 0)
  workspace.error.value = menusLoaded
    ? `无法访问菜单 ${menuCode}。当前租户未开通该产品，或当前账号缺少菜单权限。`
    : `菜单 ${menuCode} 加载超时，请刷新页面重试。`
}


function findOpenableMenu(menus: ModuleMenuItem[], menuCode: string) {
  const direct = menus.find((item) => item.code === menuCode && !item.hasChildren)
  if (direct) return direct
  const parent = menus.find((item) => item.code === menuCode)
  if (parent) {
    return findFirstOpenableChild(menus, parent.code)
  }
  return fallbackGuoxueMenu(menuCode, menus)
}

function findFirstOpenableChild(menus: ModuleMenuItem[], parentCode: string): ModuleMenuItem | null {
  const children = menus.filter((item) => item.parentCode === parentCode)
  for (const child of children) {
    if (!child.hasChildren) return child
    const leaf = findFirstOpenableChild(menus, child.code)
    if (leaf) return leaf
  }
  return null
}

function persistWorkspaceRoute() {
  if (!session.user) return
  const query = new URLSearchParams(window.location.search)
  query.delete('tab')
  query.delete('menuCode')
  query.delete('permissionSection')
  query.delete('pageCode')
  query.delete('decorationMode')
  query.delete('materialScope')
  query.delete('view')
  query.delete('paymentModule')
  query.delete('mchOrderNo')
  query.delete('payOrderNo')
  query.delete('planId')
  query.delete('returnMenuCode')
  if (activeMenu.value) {
    query.set('menuCode', activeMenu.value.code)
    const locate = pickModuleLocateTarget(moduleLocateTarget.value)
    if (locate?.view) query.set('view', locate.view)
    if (locate?.paymentModule) query.set('paymentModule', locate.paymentModule)
    if (locate?.mchOrderNo) query.set('mchOrderNo', locate.mchOrderNo)
    if (locate?.payOrderNo) query.set('payOrderNo', locate.payOrderNo)
    if (locate?.planId) query.set('planId', locate.planId)
    if (locate?.returnMenuCode) query.set('returnMenuCode', locate.returnMenuCode)
    if (activeMenu.value.code === MATERIAL_CENTER_MENU_CODE && materialScopeModuleCode.value) {
      query.set('materialScope', materialScopeModuleCode.value)
    }
    if (activeMenu.value.kind === 'decoration'
      && (activeMenu.value.code === decorationHomeMenuCode(activeMenu.value.moduleCode)
        || (activeMenu.value.code === decorationPagesMenuCode(activeMenu.value.moduleCode)
          && decorationWorkspaceMode.value === 'editor'))) {
      query.set('pageCode', decorationPageCode.value)
      query.set('decorationMode', 'editor')
    }
  } else if (activeTab.value === 'permissionManagement') {
    query.set('permissionSection', activePermissionSection.value)
  } else if (activeTab.value !== 'dashboard') {
    query.set('tab', activeTab.value)
  }
  const nextQuery = query.toString()
  const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (nextUrl !== currentUrl) {
    window.history.replaceState(null, '', nextUrl)
  }
}

function resolveModuleMenu(target: ShellNotificationTarget) {
  const menus = workspace.menuViews.value
    .flatMap((module) => module.visibleMenus)
  if (target.menuCode) {
    return findOpenableMenu(menus, canonicalWorkspaceMenuCode(target.menuCode))
  }
  const openableMenus = menus.filter((menu) => !menu.hasChildren)
  if (target.path) {
    return openableMenus.find((menu) => menu.path === target.path || menu.path.startsWith(target.path ?? '')) ?? null
  }
  if (target.moduleCode) {
    return openableMenus.find((menu) => menu.moduleCode === target.moduleCode) ?? null
  }
  return null
}

function fallbackGuoxueMenu(menuCode: string, menus: ModuleMenuItem[]) {
  const definition = guoxueFallbackMenus[menuCode]
  if (!definition || !session.hasPermission(definition.permission)) return null
  const source = menus.find((menu) => menu.moduleCode === 'guoxue')
  const module = workspace.permittedModules.value.find((item) => item.moduleCode === 'guoxue')
  if (!source && !module) return null
  return {
    ...definition,
    menuType: 'MENU',
    openMode: 'INTERNAL',
    remoteEntry: source?.remoteEntry || GUOXUE_REMOTE_ENTRY,
    exposedModule: source?.exposedModule || GUOXUE_EXPOSED_MODULE,
    hidden: false,
    enabled: true,
    moduleCode: 'guoxue',
    moduleName: source?.moduleName || module?.moduleName || '国学',
    depth: definition.parentCode ? 1 : 0,
    hasChildren: false,
    kind: 'remote' as const
  }
}

function openNotificationTarget(target: ShellNotificationTarget) {
  if (target.permissionSection) {
    setPermissionSection(target.permissionSection)
    return
  }
  if (target.menuCode === 'decoration.home' || target.path === '/decoration/home') {
    openDecorationPageEditor('home')
    return
  }
  const targetMenu = resolveModuleMenu(target)
  if (targetMenu) {
    openModuleMenu(targetMenu, target)
    return
  }
  if (target.tab) {
    setActiveTab(target.tab)
  }
}

async function loadWechatScanEnabled() {
  try {
    const domain = session.tenantDomain || session.user?.tenantDomain
    if (!domain) { wechatScanEnabled.value = false; return }
    const config = await session.createApiClient().get<{ wechatScanEnabled?: boolean }>(
      '/auth/login-config?tenantDomain=' + encodeURIComponent(domain)
    )
    wechatScanEnabled.value = config?.wechatScanEnabled ?? false
  } catch { wechatScanEnabled.value = false }
}

async function handleNotification(notification: ShellNotification) {
  notice.value = ''
  try {
    await notificationCenter.openNotification(notification)
  } catch (err) {
    workspace.error.value = err instanceof Error ? err.message : '通知状态更新失败'
  }
}

function openNotificationCenter() {
  setActiveTab('notifications')
}

async function handleModuleNotification(payload: ModuleNotificationPayload) {
  notice.value = ''
  try {
    await notificationCenter.pushNotification(payload)
    notice.value = `${payload.sender ?? payload.moduleName ?? '模块'} 发来新通知：${payload.title}`
  } catch (err) {
    workspace.error.value = err instanceof Error ? err.message : '通知写入失败'
  }
}

async function refreshCurrentView() {
  await workspace.refreshWorkspace()
  await notificationCenter.loadNotifications()
  await activeDecorationManager.value?.refresh?.()
  await activePushCenterManager.value?.refresh?.()
  await activeMaterialManager.value?.refresh?.()
  await activeContentReview.value?.refresh?.()
  await activeModuleHost.value?.refresh?.()
}

watch(workspace.error, (err) => {
  if (err) {
    message.error(err)
  }
})

watch(notice, (msg) => {
  if (msg) {
    message.success(msg)
  }
})
</script>

<template>
  <LoginView
    v-if="!session.user"
    :loading="workspace.loading.value"
    :error="workspace.error.value"
    @submit="login"
    @login-from-response="loginFromResponse"
  />

  <ShellLayout
    v-else
    :active-tab="activeTab"
    :active-permission-section="activePermissionSection"
    :active-menu-code="activeMenu?.code ?? null"
    :username="session.user.username"
    :modules="workspace.menuViews.value"
    :notifications="notificationCenter.notifications.value"
    :unread-count="notificationCenter.unreadCount.value"
    :permission-sections="permissionSections"
    :tabs="workspace.availableTabs.value"
    :loading="workspace.loading.value"
    @update:active-tab="setActiveTab"
    @open-permission-section="setPermissionSection"
    @open-menu="openModuleMenu"
    @notification-open="handleNotification"
    @notifications-mark-all-read="notificationCenter.markAllRead"
    @refresh="refreshCurrentView"
    @logout="logout"
  >
    <template #notifications="{ notifications, unreadCount, open, markAllRead }">
      <NotificationBell
        :notifications="notifications"
        :unread-count="unreadCount"
        @open="open"
        @mark-all-read="markAllRead"
        @open-center="openNotificationCenter"
      />
    </template>
    <template #agent>
      <AgentChatPanel
        v-if="session.hasPermission(SYSTEM_PERMISSIONS.agentChat)"
        title="SaaS AI Agent"
        subtitle="租户后台"
        :mode-label="saasAgent.modeLabel.value"
        placeholder="例如：打开国学AI咨询 / 新建角色 code guoxue_operator 名称 国学运营 guoxue 菜单"
        :examples="saasAgent.examples"
        :context="saasAgentContext"
        :send="saasAgent.sendChat"
        @response="saasAgent.handleResponse"
        @action="saasAgent.handleAction"
        @error="(message) => (workspace.error.value = message)"
      />
    </template>

    <ModuleUserManager
      v-if="activeMenuIsModuleUserManagement"
      :create-api-client="session.createApiClient"
      :modules="activeModuleUserModules"
      :can-create="workspace.canCreateModuleUser.value"
      :can-update="workspace.canUpdateModuleUser.value"
    />

    <SaasDecorationManager
      v-else-if="activeMenuIsDecoration"
      :key="`${activeDecorationModuleCode}:${activeDecorationMode}`"
      ref="activeDecorationManager"
      :create-api-client="session.createApiClient"
      :module-code="activeDecorationApiModuleCode"
      :can-manage="activeDecorationCanManage"
      :can-manage-templates="activeDecorationCanManageTemplates"
      :can-view-pages="activeDecorationCanView"
      :tenant-domain="session.tenantDomain"
      :initial-left-tab="activeDecorationLeftTab"
      :initial-page-code="decorationPageCode"
      :mode="activeDecorationMode"
      @edit-page="openDecorationPageEditor"
      @page-change="setDecorationPageCode"
      @notice="(message) => (notice = message)"
      @error="(message) => (workspace.error.value = message)"
    />

    <MaterialManager
      v-else-if="activeMenuIsMaterial && activeMenu"
      ref="activeMaterialManager"
      :create-api-client="session.createApiClient"
      :menu="activeMenu"
      :module-options="materialModuleOptions"
      :initial-module-code="materialScopeModuleCode"
      :can-create="session.hasPermission('system:material:create')"
      :can-update="session.hasPermission('system:material:update')"
      :can-delete="session.hasPermission('system:material:delete')"
      :can-ai-read="session.hasPermission('system:material-ai:read')"
      :can-ai-edit="session.hasPermission('system:material-ai:edit')"
      :can-manage-ai-providers="session.hasPermission('system:material-ai:provider-manage')"
      @notice="(message) => (notice = message)"
      @error="(message) => (workspace.error.value = message)"
      @scope-change="(moduleCode) => (materialScopeModuleCode = moduleCode)"
    />

    <PushCenterManager
      v-else-if="activeMenuIsPushCenter && activeMenu"
      ref="activePushCenterManager"
      :create-api-client="session.createApiClient"
      :menu="activeMenu"
      :can-manage-app="session.hasPermission('push:app:manage')"
      :can-manage-template="session.hasPermission('push:template:manage')"
      :can-manage-notification="session.hasPermission('push:notification:manage')"
      :can-send-task="session.hasPermission('push:task:send')"
      :can-manage-device="session.hasPermission('push:device:manage')"
      @notice="(message) => (notice = message)"
      @error="(message) => (workspace.error.value = message)"
    />

    <!-- 内容审核：原型阶段先放开写权限，后端权限码就绪后改为 session.hasPermission('system:content-review:*') -->
    <ContentReviewCenter
      v-else-if="activeMenuIsContentReview && activeMenu"
      ref="activeContentReview"
      :menu="activeMenu"
      :can-manage="true"
      @notice="(message) => (notice = message)"
      @error="(message) => (workspace.error.value = message)"
    />

    <ModuleHost
      v-else-if="activeMenu"
      ref="activeModuleHost"
      :menu="activeMenu"
      :session="session.user"
      :target="moduleLocateTarget ?? undefined"
      @notify="handleModuleNotification"
      @open-target="openNotificationTarget"
      @session-expired="logout"
      @register-module="notificationCenter.registerModule"
    />

    <template v-else-if="activeTab === 'dashboard'">
      <WorkspaceDashboard
        :username="session.user.username"
        :module-count="workspace.permittedModules.value.length"
        :permission-count="session.permissions.length"
        :can-read-rbac="workspace.canReadRbac.value"
        :roles="workspace.state.value.roles"
        :depts="workspace.state.value.depts"
        :menus="workspace.menuViews.value"
        :create-api-client="session.createApiClient"
        :can-view-traffic-analytics="session.hasPermission(SYSTEM_PERMISSIONS.workspaceAnalyticsView)"
        @open-module="openWorkbenchModule"
      />
      <WechatBindingCard
        :create-api-client="session.createApiClient"
        :wechat-scan-enabled="wechatScanEnabled"
      />
    </template>

    <NotificationCenter
      v-else-if="activeTab === 'notifications'"
      :notifications="notificationCenter.notifications.value"
      :total="notificationCenter.notificationTotal.value"
      :page="notificationCenter.notificationPage.value"
      :page-size="notificationCenter.notificationPageSize.value"
      :loading="notificationCenter.notificationLoading.value"
      :error="notificationCenter.notificationError.value"
      @open="handleNotification"
      @query-change="notificationCenter.loadNotifications($event)"
      @mark-all-read="notificationCenter.markAllRead"
      @remove="notificationCenter.removeNotification"
      @clear="notificationCenter.clearNotifications"
    />

    <section v-else-if="activeTab === 'permissionManagement'" class="permission-management">
      <PermissionCenter
        v-if="activePermissionSection === 'permissions' && workspace.canViewPermissions.value"
        :permissions="workspace.effectivePermissionRows.value"
        :groups="workspace.permissionGroups.value"
        :apis="workspace.apiPermissionRows.value"
        :data-scopes="workspace.dataScopeRows.value"
        :depts="workspace.state.value.depts"
      />

      <MenuManager
        v-else-if="activePermissionSection === 'menus' && workspace.canReadMenus.value"
        :groups="workspace.menuGroups.value"
        :can-create="workspace.canCreateMenus.value"
        :can-update="workspace.canUpdateMenus.value"
        :can-delete="workspace.canDeleteMenus.value"
        :loading="workspace.loading.value"
        @create="createMenu"
        @update="updateMenu"
        @reorder="reorderMenus"
        @delete="deleteMenu"
      />

      <DeptManager
        v-else-if="activePermissionSection === 'depts' && workspace.canReadDepts.value"
        :depts="workspace.state.value.depts"
        :can-create="workspace.canCreateDept.value"
        :can-update="workspace.canUpdateDept.value"
        :loading="workspace.loading.value"
        @create="createDept"
        @update="updateDept"
      />

      <RoleManager
        v-else-if="activePermissionSection === 'roles' && workspace.canReadRbac.value"
        :roles="workspace.state.value.roles"
        :menu-groups="workspace.menuGroups.value"
        :depts="workspace.state.value.depts"
        :permissions="workspace.state.value.permissionCatalog.permissions"
        :data-resources="workspace.state.value.permissionCatalog.dataResources"
        :can-create="workspace.canCreateRole.value"
        :can-update="workspace.canUpdateRole.value"
        :loading="workspace.loading.value"
        @create="createRole"
        @update="updateRole"
      />

      <UserManager
        v-else-if="activePermissionSection === 'users' && workspace.canReadRbac.value"
        :create-api-client="session.createApiClient"
        :roles="workspace.state.value.roles"
        :depts="workspace.state.value.depts"
        :menus="workspace.state.value.menus"
        :can-create="workspace.canCreateUser.value"
      />

      <ClientRoleManager
        v-else-if="activePermissionSection === 'clientRoles' && workspace.canReadClientRbac.value"
        :roles="workspace.state.value.clientRoles"
        :catalog="workspace.state.value.clientRbacCatalog"
        :modules="workspace.permittedModules.value"
        :can-create="workspace.canCreateClientRole.value"
        :can-update="workspace.canUpdateClientRole.value"
        :loading="workspace.loading.value"
        @create="createClientRole"
        @update="updateClientRole"
      />

      <ClientUserManager
        v-else-if="activePermissionSection === 'clientUsers' && workspace.canReadClientRbac.value"
        :create-api-client="session.createApiClient"
        :roles="workspace.state.value.clientRoles"
        :can-create="workspace.canCreateClientUser.value"
        :can-update="workspace.canUpdateClientUser.value"
      />

      <ModuleUserManager
        v-else-if="activePermissionSection === 'moduleUsers' && workspace.canReadModuleUsers.value"
        :create-api-client="session.createApiClient"
        :modules="workspace.permittedModules.value"
        :can-create="workspace.canCreateModuleUser.value"
        :can-update="workspace.canUpdateModuleUser.value"
      />

      <SystemParamManager
        v-else-if="activePermissionSection === 'params' && workspace.canReadParams.value"
        :params="workspace.state.value.params"
        :can-create="workspace.canCreateParam.value"
        :can-update="workspace.canUpdateParam.value"
        :can-delete="workspace.canDeleteParam.value"
        :loading="workspace.loading.value"
        @create="createParam"
        @update="updateParam"
        @delete="deleteParam"
      />

      <DictManager
        v-else-if="activePermissionSection === 'dicts' && workspace.canReadDicts.value"
        :create-api-client="session.createApiClient"
        :dict-types="workspace.state.value.dictTypes"
        :can-create="workspace.canCreateDict.value"
        :can-update="workspace.canUpdateDict.value"
        :can-delete="workspace.canDeleteDict.value"
        :can-read-items="workspace.canReadDictItems.value"
        :can-create-item="workspace.canCreateDictItem.value"
        :can-update-item="workspace.canUpdateDictItem.value"
        :can-delete-item="workspace.canDeleteDictItem.value"
        :loading="workspace.loading.value"
        @create="createDict"
        @update="updateDict"
        @delete="deleteDict"
      />

      <OpenPlatformGrantManager
        v-else-if="activePermissionSection === 'openPlatform' && workspace.canReadOpenApps.value"
        :apps="workspace.state.value.openApps"
        :grants="workspace.state.value.openAppGrants"
        :modules="workspace.permittedModules.value"
        :data-resources="workspace.state.value.permissionCatalog.dataResources"
        :can-grant="workspace.canGrantOpenApps.value"
        :loading="workspace.loading.value"
        @update="updateOpenAppGrant"
      />

      <section v-else class="panel span empty-state">
        <h2>无权访问此视图</h2>
        <p>当前账号没有打开该权限管理项所需的系统权限。</p>
      </section>
    </section>

    <BehaviorAnalyticsView
      v-else-if="activeTab === 'behaviorAnalytics'"
      :create-api-client="session.createApiClient"
      :can-view="session.hasPermission(SYSTEM_PERMISSIONS.workspaceAnalyticsView)"
    />

    <CrashIssuesView
      v-else-if="activeTab === 'crashIssues'"
      :create-api-client="session.createApiClient"
      :can-view="session.hasPermission(CRASH_ISSUES_VIEW_PERMISSION)"
    />

    <section v-else-if="!activeTabAllowed" class="panel span empty-state">
      <h2>无权访问此视图</h2>
      <p>当前账号没有打开该管理区所需的系统权限。</p>
    </section>
  </ShellLayout>
</template>

<style scoped>
.workspace-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  margin: 0 0 16px 0;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  border: 1px solid transparent;
}

.workspace-alert--error {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}

.workspace-alert--notice {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1e40af;
}

.workspace-alert__message {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.workspace-alert__close {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 150ms ease, background-color 150ms ease;
}

.workspace-alert__close:hover,
.workspace-alert__close:focus-visible {
  opacity: 1;
  background: rgba(0, 0, 0, 0.06);
  outline: none;
}
</style>
